import type { CSSProperties } from 'react';
import { Text } from '../../primitives/Text.tsx';
import styles from './Progress.module.css';

/**
 * Design system § 14 — the segmented bar.
 *
 * One segment per item in the sequence, so the structure is legible in the bar
 * itself rather than only in the counter. The current segment is 1.6x wider and
 * takes the frontier gradient, and the bar is ALWAYS accompanied by a counter
 * in `numeric-inline` — § 12's rule that colour is never the only carrier
 * applies to position as much as to outcome.
 *
 * Each completed segment is told its place in the completed RUN, because the
 * run carries one continuous grad/progress between them all rather than a
 * gradient per segment. The arithmetic is CSS's; this only supplies the two
 * numbers it cannot know.
 */

interface ProgressBarProps {
  /** How many items the sequence holds. */
  total: number;
  /** Zero-based index of the item in progress. */
  current: number;
  /**
   * The accessible name of what is progressing — this library does not know
   * whether it is a Lesson, a form or a tour.
   */
  label: string;
}

export function ProgressBar({ total, current, label }: ProgressBarProps) {
  const position = Math.min(current + 1, total);

  return (
    <div className={styles.bar}>
      <div
        className={styles.track}
        role="progressbar"
        aria-label={label}
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={position}
        aria-valuetext={`${position} of ${total}`}
      >
        {Array.from({ length: total }, (_, index) => (
          <span
            key={index}
            className={styles.segment}
            data-state={index < current ? 'complete' : index === current ? 'current' : 'empty'}
            style={
              index < current
                ? ({ '--segment-index': index, '--segment-count': current } as CSSProperties)
                : undefined
            }
          />
        ))}
      </div>

      {/* Tabular by nature: the numbers do not shift as the position updates. */}
      <Text variant="numeric-inline" tone="muted">
        {String(position).padStart(2, '0')} — {String(total).padStart(2, '0')}
      </Text>
    </div>
  );
}
